import type { JSX } from 'react';

import WebhookFormDialog from './WebhookFormDialog';
import WebhookTestDialog from './WebhookTestDialog';
import WebhooksAvailablePanelHeader from './WebhooksAvailablePanelHeader';
import WebhooksDeleteDialog from './WebhooksDeleteDialog';
import WebhooksEmptyState from './WebhooksEmptyState';
import WebhooksLimitsNotice from './WebhooksLimitsNotice';
import WebhooksLoadingTable from './WebhooksLoadingTable';
import WebhooksPartialListNotice from './WebhooksPartialListNotice';
import WebhooksTable from './WebhooksTable';
import useWebhooksAvailablePanelState from './useWebhooksAvailablePanelState';

interface WebhooksAvailablePanelContentProps {
  projectId: number;
  subProjects: { id: number; name: string }[];
}

const WebhooksAvailablePanelContent = ({ projectId, subProjects }: WebhooksAvailablePanelContentProps): JSX.Element => {
  const {
    deleteTarget,
    formError,
    formOpen,
    formState,
    handleClearDeleteTarget,
    handleClearTestTarget,
    handleConfirmDelete,
    handleFormSubmit,
    handleRefresh,
    handleResetForm,
    handleStartCreate,
    handleStartDelete,
    handleStartEdit,
    handleStartTest,
    handleTestRequest,
    isCreating,
    isDeleting,
    isError,
    isFetching,
    isLoading,
    isUpdating,
    isPartialList,
    limits,
    pagination,
    testTarget,
    webhooks,
  } = useWebhooksAvailablePanelState(projectId);

  const editingWebhook = formState?.kind === 'edit' ? formState.webhook : null;

  return (
    <section
      className="rounded-lg border border-border-subtle bg-bg-surface-alt p-4"
      aria-labelledby="webhooks-disponibles-heading"
    >
      <WebhooksAvailablePanelHeader isFetching={isFetching} onCreate={handleStartCreate} onRefresh={handleRefresh} />

      {isError ? (
        <p className="m-0 mt-4 text-sm text-error font-body" role="alert">
          No se pudieron cargar los webhooks. Intenta actualizar o revisa tu conexión.
        </p>
      ) : null}

      {isLoading ? <WebhooksLoadingTable /> : null}

      {!isLoading && !isError && webhooks.length === 0 ? <WebhooksEmptyState /> : null}

      {!isLoading && !isError && webhooks.length > 0 ? (
        <WebhooksTable
          webhooks={webhooks}
          onDelete={handleStartDelete}
          onEdit={handleStartEdit}
          onTest={handleStartTest}
        />
      ) : null}

      {limits ? <WebhooksLimitsNotice limits={limits} /> : null}

      {isPartialList ? <WebhooksPartialListNotice pagination={pagination} totalShown={webhooks.length} /> : null}

      <WebhookFormDialog
        open={formOpen}
        onClose={handleResetForm}
        onSubmit={handleFormSubmit}
        webhook={editingWebhook}
        subProjects={subProjects}
        isLoading={isCreating || isUpdating}
        error={formError}
        projectId={projectId}
      />

      <WebhookTestDialog
        key={testTarget?.id ?? 'none'}
        open={testTarget !== null}
        onClose={handleClearTestTarget}
        webhook={testTarget}
        onTest={handleTestRequest}
      />

      <WebhooksDeleteDialog
        deleteTarget={deleteTarget}
        isDeleting={isDeleting}
        onClose={handleClearDeleteTarget}
        onDelete={() => {
          handleConfirmDelete().catch(() => {});
        }}
      />
    </section>
  );
};

export default WebhooksAvailablePanelContent;
